import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper/types";
import "swiper/css";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { sanitizeHTML } from "@/lib/utils";

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, "").trim();
const uuidRegex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const getSectionText = (value: LocalCommunitiesData["text"]): string | null => {
  if (!value) return null;

  if (typeof value === "string") {
    return uuidRegex.test(value) ? null : value;
  }

  if (typeof value === "object") {
    const textFromObject =
      value.text ||
      value.item?.text ||
      value.site_content_id?.text ||
      value.directus_files_id?.description;

    if (typeof textFromObject === "string" && textFromObject.trim()) {
      return textFromObject;
    }
  }

  return null;
};

export interface LocalCommunitiesProps {
  data: LocalCommunitiesData;
}

interface CollectionItem {
  item: {
    title?: string;
    text?: string;
    url?: string;
  };
}

interface ContentCollectionItem {
  id: number;
  site_content_collections_id: {
    title: string;
    collection: CollectionItem[];
  };
}

export interface LocalCommunitiesData {
  sectionName?: string;
  heading?: { title?: string };
  text?:
    | string
    | {
        text?: string;
        item?: { text?: string };
        site_content_id?: { text?: string };
        directus_files_id?: { description?: string };
      };
  content_collection?: ContentCollectionItem[];
}

export default function LocalCommunities({ data }: LocalCommunitiesProps) {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);

  const sectionId = data.sectionName
    ? data.sectionName.replaceAll(/[^a-zA-Z0-9-_]/g, "-")
    : undefined;

  const heading = data.heading?.title;
  const sectionText = getSectionText(data.text);
  const items = data.content_collection ?? [];

  useEffect(() => {
    if (!swiperInstance || !prevRef.current || !nextRef.current) return;

    const swiper = swiperInstance as any;
    const navigationParams = swiper.params?.navigation;
    if (!navigationParams || typeof navigationParams === "boolean") return;

    navigationParams.prevEl = prevRef.current;
    navigationParams.nextEl = nextRef.current;

    if (swiper.navigation) {
      swiper.navigation.destroy();
      swiper.navigation.init();
      swiper.navigation.update();
    }
  }, [swiperInstance, items.length]);

  return (
		<LazyMotion features={domAnimation}>
			<section
				id={sectionId}
				data-name={data.sectionName}
				className='py-12 md:py-16 desktop:py-20 bg-accent-color-three-light'
			>
				{heading && (
					<m.h2
						className='font-serif font-light text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop text-white text-center mb-4 px-8'
						initial={{ opacity: 0, y: 16 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.7, ease: 'easeOut' }}
					>
						{heading}
					</m.h2>
				)}

				{sectionText && (
					<m.div
						className='text-center text-paragraph-medium text-white mb-9 px-8 [&_p]:mb-0'
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.7, ease: 'easeOut', delay: 0.15 }}
						dangerouslySetInnerHTML={sanitizeHTML(sectionText)}
					/>
				)}

				{items.length > 0 && (
					<m.div
						className='relative'
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true, amount: 0.2 }}
						transition={{ duration: 0.6, ease: 'easeOut', delay: 0.2 }}
					>
						{/* Prev button */}
						<button
							ref={prevRef}
							aria-label='Previous locations'
							className='absolute left-0 top-0 h-full w-12 z-10 flex items-center justify-center bg-primary-dark hover:bg-[#E4A43E] transition-colors duration-200 disabled:opacity-30 disabled:cursor-default'
						>
							<svg
								xmlns='http://www.w3.org/2000/svg'
								viewBox='0 0 24 24'
								fill='none'
								stroke='currentColor'
								strokeWidth='2'
								strokeLinecap='round'
								strokeLinejoin='round'
								className='w-5 h-5 text-white'
							>
								<polyline points='15 18 9 12 15 6' />
							</svg>
						</button>

						<Swiper
							modules={[Navigation]}
							spaceBetween={12}
							slidesPerView={1}
							centeredSlides={false}
							centerInsufficientSlides={true}
							watchOverflow={true}
							navigation={{
								prevEl: prevRef.current,
								nextEl: nextRef.current,
							}}
							onSwiper={setSwiperInstance}
							breakpoints={{
								480: { slidesPerView: 2 },
								768: { slidesPerView: 3 },
								1024: { slidesPerView: 4 },
								1280: { slidesPerView: 4 },
							}}
							className='!px-12'
						>
							{items.map((entry, index) => {
								const col = entry.site_content_collections_id
								const titleItem = col.collection.find(c => c.item.title)?.item
								const textItems = col.collection
									.filter(c => c.item.text)
									.map(c => stripHtml(c.item.text!))
								const urlItem = col.collection.find(
									c => 'url' in c.item && c.item.url,
								)?.item

								const name = titleItem?.title || col.title
								const url = urlItem?.url

								const cardInner = (
									<>
										<p className='font-sans font-bold text-xl md:text-2xl text-white leading-snug'>
											{name}
										</p>
										{textItems.map((txt, i) => (
											<p
												key={i}
												className='text-white/70 text-base mt-1 leading-snug font-normal'
											>
												{txt}
											</p>
										))}
									</>
								)

								const cardClass =
									'flex flex-col items-center justify-center text-center py-8 px-4 min-h-[90px] transition-colors duration-200 bg-primary-dark hover:bg-accent-color-three-light'
								const cardStyle = { border: '2px solid #f6dfbf' }

								return (
									<SwiperSlide key={entry.id ?? index}>
										{url ? (
											<a href={url} className={cardClass} style={cardStyle} target="_blank" rel="noopener noreferrer">
												{cardInner}
											</a>
										) : (
											<div className={cardClass} style={cardStyle}>
												{cardInner}
											</div>
										)}
									</SwiperSlide>
								)
							})}
						</Swiper>

						{/* Next button */}
						<button
							ref={nextRef}
							aria-label='Next locations'
							className='absolute right-0 top-0 h-full w-12 z-10 flex items-center justify-center bg-primary-dark hover:bg-[#E4A43E] transition-colors duration-200 disabled:opacity-30 disabled:cursor-default'
						>
							<svg
								xmlns='http://www.w3.org/2000/svg'
								viewBox='0 0 24 24'
								fill='none'
								stroke='currentColor'
								strokeWidth='2'
								strokeLinecap='round'
								strokeLinejoin='round'
								className='w-5 h-5 text-white'
							>
								<polyline points='9 18 15 12 9 6' />
							</svg>
						</button>
					</m.div>
				)}
			</section>
		</LazyMotion>
	)
}
